import type {
  CustomPrice,
  CustomPriceInput,
  CustomPriceUpdate,
  Page,
  Product,
  ProductAttribute,
  ProductAttributeInput,
  ProductCategory,
  ProductCategoryInput,
  ProductHistoryLine,
  ProductInput,
  ProductListItem,
  ProductListSummary,
  ProductSummary,
  ProductUpdate,
  Unit,
  UnitInput,
  VariantRef,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type { DownloadedFile } from '../../lib/api'
import { api } from '../../lib/api'
import type { Language } from '../../lib/format'
import { inventoryKeys } from '../inventory/queries'
import { pricingKeys } from './pricing'
import { toFormValues, toProductInput } from './product-variants'
import type { ProductListParams } from './search'
import { purchaseLookupKeys, variantLookupKeys } from './variant-picker'

export const catalogKeys = {
  products: ['products'] as const,
  productLists: () => [...catalogKeys.products, 'list'] as const,
  productList: (query: ProductListParams) => [...catalogKeys.productLists(), query] as const,
  listSummary: (query: object) => [...catalogKeys.products, 'summary', query] as const,
  product: (id: string) => [...catalogKeys.products, 'detail', id] as const,
  productSummary: (id: string) => [...catalogKeys.products, 'detail', id, 'summary'] as const,
  productHistory: (id: string, kind: 'sales' | 'purchases', query: ProductHistoryParams) =>
    [...catalogKeys.products, 'detail', id, kind, query] as const,
  units: ['units'] as const,
  categories: ['product-categories'] as const,
  attributes: ['product-attributes'] as const,
  variant: (id: string) => ['product-variants', 'detail', id] as const,
  customPrices: ['custom-prices'] as const,
  productCustomPrices: (productId: string, query: CustomPriceParams) =>
    [...catalogKeys.customPrices, 'product', productId, query] as const,
}

const path = (base: string, id: string, action?: string) =>
  `${base}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

// ── products ─────────────────────────────────────────────────────────────────

/** The list's filters as the API takes them (the list, its counts and its export share them). */
function filterQuery(query: ProductListParams) {
  return {
    q: query.q,
    type: query.type,
    categoryId: query.categoryId,
    warehouseId: query.warehouseId,
  }
}

export function productListQueryOptions(query: ProductListParams) {
  return queryOptions({
    queryKey: catalogKeys.productList(query),
    queryFn: ({ signal }) =>
      api.get<Page<ProductListItem>>('/products', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          view: query.view,
          stock: query.stock,
          ...filterQuery(query),
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/** The counts on the list's view tabs, for the filters shown (`GET /products/summary`). */
export function productListSummaryQueryOptions(query: ProductListParams) {
  const filters = filterQuery(query)
  return queryOptions({
    queryKey: catalogKeys.listSummary(filters),
    queryFn: ({ signal }) => api.get<ProductListSummary>('/products/summary', { signal, query: filters }),
    placeholderData: keepPreviousData,
  })
}

/** Every product of the view as filtered and sorted now, as CSV (`products.export`). */
export function exportProducts(query: ProductListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/products/export', {
    query: {
      format: 'csv',
      lang: language,
      sort: query.sort,
      view: query.view,
      stock: query.stock,
      ...filterQuery(query),
    },
    fallbackName: 'products.csv',
  })
}

/** The product 360's figures; each section is null without its permission (`GET /products/:id/summary`). */
export function productSummaryQueryOptions(id: string) {
  return queryOptions({
    queryKey: catalogKeys.productSummary(id),
    queryFn: ({ signal }) => api.get<ProductSummary>(path('/products', id, 'summary'), { signal }),
  })
}

export interface ProductHistoryParams {
  page: number
  pageSize: number
  variantId?: string | undefined
  from?: string | undefined
  to?: string | undefined
}

/** Posted sales (sales.read) or purchase (purchasing.read) lines of the product, newest first. */
export function productHistoryQueryOptions(
  id: string,
  kind: 'sales' | 'purchases',
  query: ProductHistoryParams,
) {
  return queryOptions({
    queryKey: catalogKeys.productHistory(id, kind, query),
    queryFn: ({ signal }) =>
      api.get<Page<ProductHistoryLine>>(path('/products', id, kind), {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          variantId: query.variantId,
          from: query.from,
          to: query.to,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

export function productQueryOptions(id: string) {
  return queryOptions({
    queryKey: catalogKeys.product(id),
    queryFn: ({ signal }) => api.get<Product>(path('/products', id), { signal }),
  })
}

/**
 * GET /product-variants/:id: one variant's label, unit and serial tracking, under the lookup
 * permissions (a stock clerk without `products.read`, or Catalog off). Forms opened on a variant
 * fill their line from it.
 */
export function variantRefQueryOptions(id: string) {
  return queryOptions({
    queryKey: catalogKeys.variant(id),
    queryFn: ({ signal }) => api.get<VariantRef>(path('/product-variants', id), { signal }),
  })
}

/**
 * What prices a document line: the pickers' lookups (they carry each variant's units, grid and the
 * customer's custom prices) and the prices a document re-reads for its customer (ADR-045).
 */
function invalidatePricing(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: variantLookupKeys.all }),
    queryClient.invalidateQueries({ queryKey: purchaseLookupKeys.all }),
    queryClient.invalidateQueries({ queryKey: pricingKeys.all }),
  ])
}

/**
 * Product names, prices, packs and reorder levels show up in lists, pickers, category counts and stock
 * screens: refresh them all after a product changes.
 */
function invalidateProductViews(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: catalogKeys.productLists() }),
    queryClient.invalidateQueries({ queryKey: [...catalogKeys.products, 'summary'] }),
    queryClient.invalidateQueries({ queryKey: catalogKeys.categories }),
    invalidatePricing(queryClient),
    queryClient.invalidateQueries({ queryKey: ['product-variants', 'detail'] }),
    queryClient.invalidateQueries({ queryKey: inventoryKeys.stock }),
    queryClient.invalidateQueries({ queryKey: inventoryKeys.movements }),
    // A pack removed takes its custom prices with it; units count the products using them.
    queryClient.invalidateQueries({ queryKey: catalogKeys.customPrices }),
    queryClient.invalidateQueries({ queryKey: catalogKeys.units }),
  ])
}

function useProductMutation<TInput>(request: (input: TInput) => Promise<Product>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (product) => {
      queryClient.setQueryData(catalogKeys.product(product.id), product)
      void invalidateProductViews(queryClient)
    },
  })
}

export function useCreateProduct() {
  return useProductMutation((input: ProductInput) => api.post<Product>('/products', input))
}

export function useUpdateProduct(id: string) {
  return useProductMutation((input: ProductUpdate) => api.put<Product>(path('/products', id), input))
}

export function useSetProductArchived(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (archived: boolean) =>
      api.post<void>(path('/products', id, archived ? 'archive' : 'restore')),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: catalogKeys.product(id) }),
        invalidateProductViews(queryClient),
      ]),
  })
}

/** Archive or restore any product (a list row's menu). */
export function useSetProductArchivedById() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.post<void>(path('/products', id, archived ? 'archive' : 'restore')),
    onSuccess: (_, { id }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: catalogKeys.product(id) }),
        invalidateProductViews(queryClient),
      ]),
  })
}

/**
 * A variant's Retail price of the base unit (its list price, what the till and invoices start from),
 * changed from outside the product form (a purchase line, G21): reads the product as it is now and saves
 * it back through `PUT /products/:id` with only that price changed, so the catalogue's own rules, audit
 * and version check apply. Needs `products.update`.
 */
export function useSetVariantListPrice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ variantId, price }: { variantId: string; price: string }) => {
      const ref = await queryClient.fetchQuery(variantRefQueryOptions(variantId))
      const product = await api.get<Product>(path('/products', ref.productId))
      const values = toFormValues(product)
      const row = values.variants.find((v) => v.variantId === variantId)
      if (!row) throw new Error('The variant is no longer part of its product')
      row.price = price
      const input: ProductUpdate = { ...toProductInput(values), version: product.version }
      return api.put<Product>(path('/products', product.id), input)
    },
    onSuccess: (product) => {
      queryClient.setQueryData(catalogKeys.product(product.id), product)
      void invalidateProductViews(queryClient)
    },
  })
}

/**
 * POST /products/:id/release-codes: an archived product gives up its SKUs (renamed `…-ARC-…`) and barcodes,
 * so another product can take them (G20).
 */
export function useReleaseProductCodes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/products', id, 'release-codes')),
    onSuccess: (_, id) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: catalogKeys.product(id) }),
        invalidateProductViews(queryClient),
      ]),
  })
}

// ── custom prices (ADR-045) ──────────────────────────────────────────────────

export interface CustomPriceParams {
  page: number
  pageSize: number
}

/** A product's custom prices (every customer and group, expired ones too), newest first. */
export function productCustomPricesQueryOptions(productId: string, query: CustomPriceParams) {
  return queryOptions({
    queryKey: catalogKeys.productCustomPrices(productId, query),
    queryFn: ({ signal }) =>
      api.get<Page<CustomPrice>>('/custom-prices', {
        signal,
        query: { productId, page: query.page, pageSize: query.pageSize },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Custom prices change what documents charge: refresh the lists and everything that prices a line. */
function useCustomPriceMutation<TInput, TResult>(request: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: catalogKeys.customPrices }),
        invalidatePricing(queryClient),
      ]),
  })
}

export function useCreateCustomPrice() {
  return useCustomPriceMutation((input: CustomPriceInput) => api.post<CustomPrice>('/custom-prices', input))
}

export function useUpdateCustomPrice() {
  return useCustomPriceMutation(({ id, input }: { id: string; input: CustomPriceUpdate }) =>
    api.put<CustomPrice>(path('/custom-prices', id), input),
  )
}

export function useDeleteCustomPrice() {
  return useCustomPriceMutation((id: string) => api.delete<void>(path('/custom-prices', id)))
}

// ── units ────────────────────────────────────────────────────────────────────

export const unitsQueryOptions = queryOptions({
  queryKey: catalogKeys.units,
  queryFn: ({ signal }) => api.get<Unit[]>('/units', { signal }),
  staleTime: 5 * 60_000,
})

export function useSaveUnit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string | null
      input: UnitInput & { version?: number }
    }): Promise<Unit> => (id ? api.put<Unit>(path('/units', id), input) : api.post<Unit>('/units', input)),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: catalogKeys.units }),
        // Unit symbols appear on product rows, stock levels and the units a line is priced in.
        queryClient.invalidateQueries({ queryKey: catalogKeys.products }),
        queryClient.invalidateQueries({ queryKey: inventoryKeys.stock }),
        invalidatePricing(queryClient),
      ]),
  })
}

export function useArchiveUnit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/units', id, 'archive')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogKeys.units }),
  })
}

export function useRestoreUnit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/units', id, 'restore')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogKeys.units }),
  })
}

// ── categories ───────────────────────────────────────────────────────────────

export const categoriesQueryOptions = queryOptions({
  queryKey: catalogKeys.categories,
  queryFn: ({ signal }) => api.get<ProductCategory[]>('/product-categories', { signal }),
  staleTime: 60_000,
})

export function useSaveCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: ProductCategoryInput & { version?: number } }) =>
      id
        ? api.put<ProductCategory>(path('/product-categories', id), input)
        : api.post<ProductCategory>('/product-categories', input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: catalogKeys.categories }),
        // Category names appear on product rows and records.
        queryClient.invalidateQueries({ queryKey: catalogKeys.products }),
      ]),
  })
}

export function useArchiveCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/product-categories', id, 'archive')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogKeys.categories }),
  })
}

export function useRestoreCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/product-categories', id, 'restore')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogKeys.categories }),
  })
}

// ── attributes ───────────────────────────────────────────────────────────────

export const attributesQueryOptions = queryOptions({
  queryKey: catalogKeys.attributes,
  queryFn: ({ signal }) => api.get<ProductAttribute[]>('/product-attributes', { signal }),
  staleTime: 60_000,
})

export function useSaveAttribute() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: ProductAttributeInput }) =>
      id
        ? api.put<ProductAttribute>(path('/product-attributes', id), input)
        : api.post<ProductAttribute>('/product-attributes', input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: catalogKeys.attributes }),
        // Renamed values change derived variant names on product records.
        queryClient.invalidateQueries({ queryKey: catalogKeys.products }),
      ]),
  })
}

export function useArchiveAttribute() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/product-attributes', id, 'archive')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogKeys.attributes }),
  })
}

export function useRestoreAttribute() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/product-attributes', id, 'restore')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogKeys.attributes }),
  })
}
